import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { Component, inject, OnInit } from '@angular/core';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import { TestEntity } from '../test.entity';
import { firstValueFrom, Observable, of } from 'rxjs';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { Store } from '@ngxs/store';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatOption, MatSelect } from '@angular/material/select';
import {
  normalizeManyRelations,
  normalizeOneRelation,
} from '../../core/decorators/relation.decorator';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../subject/subject.entity';
import { SequenceEntityService } from '../../sequence/services/sequence.entity.service';
import { SequenceEntity } from '../../sequence/sequence.entity';
import { EntityStateModel } from '../../core/state/entity.state';
import { map } from 'rxjs/operators';
import { MultiComboEntitySearchComponent } from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { LearningUnitJsonModel } from '../../learning-unit/learning-unit.json.model';
import { MatDivider } from '@angular/material/divider';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'pixl-form-dialog-test',
  templateUrl: './form-dialog-test.component.html',
  imports: [
    CommonModule,
    MatDialogContent,
    MatFormField,
    MatDialogTitle,
    MatLabel,
    MatFormField,
    MatInput,
    MatFormField,
    MatDialogActions,
    ReactiveFormsModule,
    MatButton,
    MatSelect,
    AsyncPipe,
    MatOption,
    FormsModule,
    MultiComboEntitySearchComponent,
    CdkDrag,
    CdkDropList,
    MatDivider,
    TranslocoModule,
  ],
  styleUrls: ['./form-dialog-test.component.scss'],
})
export class FormDialogTestComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<FormDialogTestComponent>>(MatDialogRef);

  data? = inject<Partial<TestEntity>>(MAT_DIALOG_DATA);

  form: FormGroup;
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  sequences$!: Observable<SequenceEntity[]>;

  private store = inject(Store);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private sequenceService = inject(SequenceEntityService);

  selectedSequences: SequenceEntity[] = [];

  currentPage = 1;
  pageSize = 20;

  sequenceSearchQuery: string | null = null;

  constructor() {
    const data = this.data;

    this.form = this.fb.group({
      name: [data?.name || ''],
      consumerId: [data?.consumerId || ''],
      subjectId: [data?.subjectId || ''],
      sequenceIds: [data?.sequenceIds || []],
      kind: [data?.kind || ''],
      consumerData: [data?.consumerData || []],
      copyRestricted: [data?.copyRestricted || ''],
      approval: [data?.approval || ''],
      active: [data?.active || ''],
      version: [data?.version || ''],
      updated: [data?.updated || ''],
      changedBy: [data?.changedBy || ''],
      consumers: [normalizeOneRelation(data?.consumers)],
      subjects: [normalizeOneRelation(data?.subjects)],
      sequences: [normalizeManyRelations(data?.sequences)],
    });
  }

  async ngOnInit() {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = this.store.select(
      this.consumerService.allByQuery(consumerQuery),
    );
    await this.consumerService.ensureLoaded(consumerQuery);

    const subjectQuery = { sort: 'name' };
    this.subjects$ = this.store.select(
      this.subjectService.allByQuery(subjectQuery),
    );
    await this.subjectService.ensureLoaded(subjectQuery);

    const sequencePageQuery = {
      'page[size]': this.pageSize,
      'page[number]': this.currentPage,
      'sort': 'name',
    };
    await this.sequenceService.ensureLoaded(sequencePageQuery);

    const selectedSequenceIds = normalizeManyRelations(
      this.data?.sequences,
    ) as string[];

    // Resolve full entities from store
    const state = this.store.selectSnapshot(
      this.sequenceService.stateClass,
    ) as EntityStateModel<SequenceEntity>;
    this.selectedSequences = selectedSequenceIds
      .map((id) => state.entities[id])
      .filter((e): e is SequenceEntity => !!e);

    // Observable for all sequences
    this.sequences$ = this.store
      .select(this.sequenceService.allByQuery(sequencePageQuery))
      .pipe(
        map((paginated) => {
          const combined = [...this.selectedSequences, ...paginated].filter(
            (seq, index, self) =>
              index === self.findIndex((s) => s.id === seq.id),
          );
          return combined;
        }),
      );

    this.form
      .get('sequences')
      ?.setValue(this.selectedSequences.map((s) => s.id));
  }

  receiveSequenceData(selected: SequenceEntity[] | null) {
    this.selectedSequences = selected ?? [];
    this.form
      .get('sequences')
      ?.setValue(this.selectedSequences.map((s) => s.id));
  }

  async receiveSequenceSearchQuery(query: string) {
    this.sequenceSearchQuery = query?.trim() || null;

    if (this.sequenceSearchQuery && this.sequenceSearchQuery.length > 3) {
      // search mode
      const searchParams = { 'filter[nameLike]': this.sequenceSearchQuery };
      await this.sequenceService.ensureLoaded(searchParams);

      this.sequences$ = this.store
        .select(this.sequenceService.allByQuery(searchParams))
        .pipe(
          map((results) => results), // optionally include selectedSequences if you want
        );
    }
    else {
      // default paginated mode
      const sequencePageQuery = {
        'page[size]': this.pageSize,
        'page[number]': this.currentPage,
        'sort': 'name',
      };
      this.sequences$ = this.store
        .select(this.sequenceService.allByQuery(sequencePageQuery))
        .pipe(
          map((paginated) => {
            const combined = [...this.selectedSequences, ...paginated].filter(
              (seq, index, self) =>
                index === self.findIndex((s) => s.id === seq.id),
            );
            return combined;
          }),
        );
    }
  }

  async loadMoreSequences(event: { pageNumber: number, pageSize: number }) {
    if (!this.sequenceSearchQuery) {
      const sequencePageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'name',
      };

      // Load next page from API
      await this.sequenceService.ensureLoaded(sequencePageQuery);

      // Get current sequences (previously loaded + selected)
      const currentSequences = await firstValueFrom(this.sequences$ ?? of([]));

      // Combine previously loaded + new ones from store
      this.sequences$ = this.store
        .select(this.sequenceService.allByQuery(sequencePageQuery))
        .pipe(
          map((paginated) => {
            const combined = [
              ...this.selectedSequences,
              ...currentSequences,
              ...paginated,
            ].filter(
              (seq, index, self) =>
                index === self.findIndex((s) => s.id === seq.id),
            );
            return combined;
          }),
        );
    }
  }

  reOrderSelectedSequences(event: CdkDragDrop<LearningUnitJsonModel[]>) {
    // Reorder the array in place
    moveItemInArray(
      this.selectedSequences,
      event.previousIndex,
      event.currentIndex,
    );

    // Update the form control so changes are tracked
    this.form
      .get('sequences')
      ?.setValue(this.selectedSequences.map((seq) => seq.id));
  }

  save(): void {
    if (this.form.valid) {
      const formValue = this.form.value;

      const updatedTest = Object.assign(
        new TestEntity(),
        { id: this.data?.id },
        formValue,
        {
          consumers: formValue.consumers ? { id: formValue.consumers } : [],
          subjects: formValue.subjects ? { id: formValue.subjects } : [],
          sequences: (formValue.sequences as string[])?.length ?
            (formValue.sequences as string[]).map((id) => ({ id })) :
            [],
        },
      );

      this.dialogRef.close(updatedTest);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  trackById(index: number, item: SequenceEntity) {
    return item.id;
  }
}
